import React, { FC, ReactElement, useState, useRef } from 'react';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import GoogleMapReact, { Coords } from 'google-map-react';
// Components
import MeetingPointPinComponent from './meetingPointPinComponent';
// Modules
import { useGetSearchClosestAddress } from '../../core/services/geolocation';
// Resources
import envConfig from '../../config/currentEnv';
import { fitBounds, getCustomMap, getLocationFromGoogle, mapUtils, setCenter } from '../../utils/map';
import { Address, AddressType, Location, SearchAddress } from '../../core/apiTypes';
import { useTheme } from '../../context/ThemeContext';

interface SelectPointMapProps {
    children?: React.ReactNode;
    onApiLoaded?: (map, maps) => void;
    onSelectPosition: (name, address) => void;
    center?: Coords;
    zoom?: number;
    radius?: number;
    type?: AddressType;
    useCustomCenter?: boolean;
}

const MAP_HEIGHT = 300;

const SelectPointMap: FC<SelectPointMapProps> = (props): ReactElement => {
    const {
        children,
        center = mapUtils.defaultCenter,
        radius = null,
        zoom = mapUtils.defaultZoom,
        type,
        useCustomCenter = true,
        onApiLoaded,
        onSelectPosition,
    } = props;

    const { mode } = useTheme();

    const [googleMap, setMap] = useState(null);
    const [customCenter, setCustomCenter] = useState<Location>(center);
    const [shouldFetch, setShouldFetch] = useState<boolean>(false);

    const radiusDraw = useRef(null);

    React.useEffect(() => {
        if (googleMap?.map && googleMap?.maps) {
            if (!radius) {
                googleMap.map.setZoom(zoom * 1.3);
            }
            setCenter(googleMap.map, center ?? mapUtils.defaultCenter);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [googleMap, radius, radiusDraw]);

    React.useEffect(() => {
        if (center) {
            setCustomCenter(center);
        }
    }, [center]);

    React.useEffect(() => {
        if (radiusDraw.current) {
            radiusDraw.current.setMap(null);
            radiusDraw.current = null;
        }
        if (radius && googleMap?.map && googleMap?.maps) {
            radiusDraw.current = new googleMap.maps.Circle({
                map: googleMap.map,
                center: customCenter,
                radius: radius * 1000,
                strokeColor: '#FF0000',
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: '#FF0000',
                fillOpacity: 0.35,
            });
            fitBounds(googleMap.map, radiusDraw.current.getBounds());
        }
        return () => {
            if (radiusDraw.current) {
                radiusDraw.current.setMap(null);
                radiusDraw.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [radius, center, googleMap]);

    // Request address from current location
    useGetSearchClosestAddress<SearchAddress>({
        enabled: shouldFetch,
        variables: {
            location: customCenter,
        },
        onSuccess: (data: SearchAddress) => {
            setShouldFetch(false);
            const { name, ...rest }: SearchAddress = data;
            const address: Address = { ...rest, type: type ?? AddressType.FAVORITE };
            if (useCustomCenter) {
                address.location = customCenter;
            }
            onSelectPosition(data.details?.full_name, address);
        },
    });

    const onGoogleReady = (map, maps) => {
        setMap({ map, maps });
        if (onApiLoaded) {
            onApiLoaded(map, maps);
        }
    };

    const classes = useStyles();

    return (
        <div className={classes.map}>
            <GoogleMapReact
                bootstrapURLKeys={{ key: envConfig.currentEnv.googleMapKey }}
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={({ map, maps }) => onGoogleReady(map, maps)}
                onDrag={(map) => {
                    setShouldFetch(false);
                    setCustomCenter(getLocationFromGoogle(map.getCenter()));
                }}
                onDragEnd={(map) => {
                    setCustomCenter(getLocationFromGoogle(map.getCenter()));
                    setShouldFetch(true);
                }}
                // defaultCenter={center}
                center={center}
                // defaultZoom={zoom}
                zoom={zoom}
                options={{ fullscreenControl: false, zoomControl: false, styles: getCustomMap(mode) }}
            >
                {googleMap && <MeetingPointPinComponent lat={customCenter.lat} lng={customCenter.lng} />}
                {children}
            </GoogleMapReact>
        </div>
    );
};

const useStyles = makeStyles(() =>
    createStyles({
        map: {
            display: 'flex',
            width: '100%',
            height: MAP_HEIGHT,
            maxHeight: MAP_HEIGHT,
            minHeight: MAP_HEIGHT,
        },
    }),
);

export default SelectPointMap;
